import React, { Fragment, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { useBusinessDashboard } from '~/hooks/apis/business-traveller/useBusinessDashboard';

import BusinessDashboardEmployees from './BusinessDashboardEmployees';

const tiers: Array<App.LuxLoyaltyTier> = ['bronze', 'silver', 'gold', 'platinum', 'infinite'];
const money = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });

export default function BusinessDashboard() {
  const queryClient = useQueryClient();
  const { data, isLoading, error, isFetching } = useBusinessDashboard(true);
  const [search, setSearch] = useState('');
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const businesses = useMemo(
    () =>
      (data?.businesses ?? []).filter((business) =>
        selectedBusinessId
          ? business.id === selectedBusinessId
          : `${business.name} ${business.id}`.toLowerCase().includes(search.toLowerCase()),
      ),
    [data, search, selectedBusinessId],
  );
  function toggle(id: string) {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }
  return (
    <Stack gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h5">Business performance</Typography>
          <Typography color="text.secondary">
            Lifetime business travel · TTV and margin in AUD, before refunds
          </Typography>
        </Box>
        <Button
          onClick={() => queryClient.invalidateQueries({ queryKey: ['business-traveller'] })}
          disabled={isFetching}
        >
          Reload
        </Button>
      </Stack>
      <Autocomplete
        freeSolo
        openOnFocus
        forcePopupIcon
        size="small"
        options={data?.businesses ?? []}
        value={data?.businesses.find((business) => business.id === selectedBusinessId) ?? null}
        inputValue={search}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
        getOptionKey={(option) => (typeof option === 'string' ? option : option.id)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        filterOptions={(options, { inputValue }) =>
          options.filter((business) =>
            `${business.name} ${business.id}`.toLowerCase().includes(inputValue.toLowerCase()),
          )
        }
        onInputChange={(_, value, reason) => {
          setSearch(value);
          if (reason === 'input' || reason === 'clear') setSelectedBusinessId(null);
          setPage(0);
        }}
        onChange={(_, value) => {
          setSelectedBusinessId(typeof value === 'string' ? null : (value?.id ?? null));
          setSearch(typeof value === 'string' ? value : (value?.name ?? ''));
          setPage(0);
        }}
        renderOption={({ key, ...optionProps }, business) => (
          <Box component="li" key={key} {...optionProps}>
            <Stack>
              <Typography>{business.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {business.id}
              </Typography>
            </Stack>
          </Box>
        )}
        renderInput={(params) => <TextField {...params} label="Search business name or ID" />}
      />
      {isLoading && <CircularProgress />}
      {error && <Alert severity="error">{error.message}</Alert>}
      {data && (
        <Paper variant="outlined">
          <TableContainer>
            <Table aria-label="Business lifetime performance">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Business name</TableCell>
                  {[
                    'TTV (AUD)',
                    'Margin (AUD)',
                    'Orders',
                    'Employee count',
                    ...tiers.map((tier) => `Société ${tier}`),
                    'No status / unknown',
                  ].map((label) => (
                    <TableCell key={label} align="right" sx={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {businesses.slice(page * 25, page * 25 + 25).map((business) => (
                  <Fragment key={business.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => toggle(business.id)}
                          aria-expanded={expanded.has(business.id)}
                          aria-label={`Expand ${business.name}`}
                        >
                          {expanded.has(business.id) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ minWidth: 260 }}>
                        <Typography fontWeight={600}>{business.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {business.id}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{business.ttv === null ? '—' : money.format(business.ttv)}</TableCell>
                      <TableCell align="right">
                        {business.margin === null ? '—' : money.format(business.margin)}
                      </TableCell>
                      <TableCell align="right">{business.orders?.toLocaleString() ?? '—'}</TableCell>
                      <TableCell align="right">
                        {business.snapshotAvailable ? business.employees.length : '—'}
                      </TableCell>
                      {tiers.map((tier) => (
                        <TableCell key={tier} align="right">
                          {business.snapshotAvailable
                            ? business.employees.filter((employee) => employee.tier === tier).length
                            : '—'}
                        </TableCell>
                      ))}
                      <TableCell align="right">
                        {business.snapshotAvailable
                          ? business.employees.filter((employee) => !employee.tier).length
                          : '—'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={12} sx={{ p: 0, border: expanded.has(business.id) ? undefined : 0 }}>
                        <Collapse in={expanded.has(business.id)} unmountOnExit>
                          {expanded.has(business.id) && <BusinessDashboardEmployees business={business} />}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={businesses.length}
            page={page}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
            onPageChange={(_, next) => setPage(next)}
          />
        </Paper>
      )}
    </Stack>
  );
}
